import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { api } from '../../api';
import queryClient from '../../query-client';
import {
    CustomWorksheetTemplate,
    CustomWorksheetTemplateOverview,
    CustomWorksheetTemplateStatus,
    CWTBehaviourEnum,
    CWTSchema,
} from '../../types/custom-worksheet-v2';
import { EntityTypeEnum } from '../../types/entity';

const RESOURCE_KEY = 'custom-worksheet-templates';

/**
 * CREATE
 */
export type CreateRequest = {
    name: string;
    description?: string | null;
    tags: string[];
    schema?: CWTSchema;
    status?: 'PUBLISHED' | 'UNPUBLISHED';
    entityTypes?: EntityTypeEnum[];
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum | null;
};

export type CreateResponse = { template: CustomWorksheetTemplate };

async function create(req: CreateRequest): Promise<CreateResponse> {
    const { data } = await api.post(`/templates/custom-worksheets`, req);
    return data;
}

export function useCreateCalculationTemplate(): UseMutationResult<CreateResponse, AxiosError, CreateRequest, unknown> {
    return useMutation((req) => create(req), {
        onSettled: () => {
            queryClient.invalidateQueries([RESOURCE_KEY]);
        },
    });
}

/**
 * GET
 */
type GetParams = {
    search?: string;
    status?: CustomWorksheetTemplateStatus;
    entityType?: EntityTypeEnum[];
    allowAsSchedule?: boolean;
    workpaperId?: string;
};

type GetResponse = {
    templates: CustomWorksheetTemplateOverview[];
    totalItems: number;
};

async function get(params: GetParams): Promise<GetResponse | null> {
    const { data } = await api.get(`/templates/custom-worksheets`, { params });
    return data;
}

export function useCalculationTemplates(params: GetParams): UseQueryResult<GetResponse, AxiosError> {
    return useQuery([RESOURCE_KEY, ...Object.values(params)], () => get(params), {
        keepPreviousData: true,
    });
}

/**
 * UPDATE
 */
export type UpdateParams = Partial<{
    name: string;
    description: string | null;
    tags: string[];
    status: CustomWorksheetTemplateStatus;
    entityTypes: EntityTypeEnum[];
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum | null;
}>;

async function update(templateId: string, req: UpdateParams): Promise<{ results: [] }> {
    const { data } = await api.patch(`/templates/custom-worksheets/${templateId}`, req);
    return data;
}

export function useUpdateCalculationTemplate(templateId: string): UseMutationResult<{ results: [] }, AxiosError, UpdateParams, unknown> {
    return useMutation((req: UpdateParams) => update(templateId, req), {
        onSettled: () => {
            queryClient.invalidateQueries([RESOURCE_KEY, templateId]);
        },
    });
}
